import { ExternalLink, Mail, Linkedin, Phone } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/vywq6zhw_BLACKLOGO.png";
const ICON_URL = "https://customer-assets.emergentagent.com/job_pay-next-gen/artifacts/1oy2wqev_ICON.png";

const DOCS_URL = `https://docs.google.com/viewer?url=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/docs`)}`;
const PRIVACY_URL = `https://docs.google.com/viewer?url=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/privacy`)}`;
const TERMS_URL = `https://docs.google.com/viewer?url=${encodeURIComponent(`${process.env.REACT_APP_BACKEND_URL}/api/terms`)}`;

const navLinks = [
  { label: 'Problem', href: '#problem' },
  { label: 'Solution', href: '#solution' },
  { label: 'Features', href: '#features' },
  { label: 'How It Works', href: '#how-it-works' },
  { label: 'For Who', href: '#audience' },
  { label: 'About', href: '#about' },
  { label: 'Request Demo', href: '#demo' },
  { label: 'Sandbox', href: '#sandbox' },
  { label: 'Docs', href: DOCS_URL, external: true },
];

function scrollTo(href) {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

export default function Footer() {
  return (
    <footer data-testid="footer" className="relative bg-black border-t border-[#1A1A1A]">
      {/* Ticker bar */}
      <div className="overflow-hidden bg-[#00FF00] py-2">
        <div
          className="flex gap-12 whitespace-nowrap"
          style={{ animation: 'ticker 30s linear infinite' }}
        >
          {Array.from({ length: 6 }).flatMap(() => [
            'SOVEREIGN DIGITAL LAYER',
            'FEDNOW-NATIVE',
            'BANK-OWNED REVENUE',
            'WHITE-LABEL EWALLET',
            'INSTANT MONEY. REAL CONTROL.',
            'COMMUNITY BANKING EMPOWERED',
          ]).map((text, i) => (
            <span
              key={i}
              className="text-black text-xs font-black uppercase tracking-widest font-mono"
            >
              {text} &bull;
            </span>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <img src={ICON_URL} alt="NX" className="h-12 w-12 object-contain" />
              <div>
                <div className="text-white font-bold text-xs uppercase tracking-widest"
                  style={{ fontFamily: 'Unbounded, sans-serif' }}>
                  NexGenPay
                </div>
                <div className="text-[#00FF00] text-xs font-mono uppercase tracking-widest opacity-70">
                  eWallet
                </div>
              </div>
            </div>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              The sovereign digital layer for community banking. FedNow-native, white-label, bank-owned. Empower your community. Control your future.
            </p>
            <div className="flex gap-3">
              <a href="mailto:calvin@nexgenpay.co" data-testid="footer-email-link"
                className="p-2 border border-[#1A1A1A] text-[#A3A3A3] hover:border-[#00FF00] hover:text-[#00FF00] transition-colors duration-300">
                <Mail size={16} />
              </a>
              <a href="https://www.linkedin.com/in/calvinnfrancis/" target="_blank" rel="noopener noreferrer" data-testid="footer-linkedin-link"
                className="p-2 border border-[#1A1A1A] text-[#A3A3A3] hover:border-[#00FF00] hover:text-[#00FF00] transition-colors duration-300">
                <Linkedin size={16} />
              </a>
            </div>
          </div>

          {/* Navigation */}
          <div>
            <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Navigation
            </h4>
            <div className="flex flex-col gap-3">
              {navLinks.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  onClick={link.external ? undefined : (e) => { e.preventDefault(); scrollTo(link.href); }}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  className="text-[#A3A3A3] text-sm hover:text-[#00FF00] transition-colors duration-200 flex items-center gap-2 group"
                  style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                  <span className="w-0 group-hover:w-3 h-px bg-[#00FF00] transition-all duration-300" />
                  {link.label}
                </a>
              ))}
            </div>
          </div>

          {/* CTA */}
          <div>
            <h4 className="text-white font-bold text-xs uppercase tracking-widest mb-6"
              style={{ fontFamily: 'Unbounded, sans-serif' }}>
              Get Started
            </h4>
            <p className="text-[#A3A3A3] text-sm leading-relaxed mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Ready to transform your bank's digital payment infrastructure? Schedule a demo with our team today.
            </p>

            {/* Contact details */}
            <div className="flex flex-col gap-3 mb-6">
              <a
                href="tel:9549532937"
                data-testid="footer-phone-link"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-[#00FF00] transition-colors duration-300 group"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <div className="p-2 border border-[#1A1A1A] group-hover:border-[#00FF00] transition-colors duration-300 flex-shrink-0">
                  <Phone size={14} className="text-[#00FF00]" />
                </div>
                <span className="text-sm">954-953-2937</span>
              </a>
              <a
                href="mailto:calvin@nexgenpay.co"
                data-testid="footer-contact-email-link"
                className="flex items-center gap-3 text-[#A3A3A3] hover:text-[#00FF00] transition-colors duration-300 group"
                style={{ fontFamily: 'Manrope, sans-serif' }}
              >
                <div className="p-2 border border-[#1A1A1A] group-hover:border-[#00FF00] transition-colors duration-300 flex-shrink-0">
                  <Mail size={14} className="text-[#00FF00]" />
                </div>
                <span className="text-sm">calvin@nexgenpay.co</span>
              </a>
            </div>

            <button
              onClick={() => scrollTo('#demo')}
              data-testid="footer-cta-btn"
              className="btn-neon w-full text-xs flex items-center justify-center gap-2"
              style={{ fontFamily: 'Unbounded, sans-serif', padding: '14px 24px' }}
            >
              Request a Demo <ExternalLink size={14} />
            </button>
            <div className="mt-4 border border-[#1A1A1A] p-4" style={{ background: '#0A0A0A' }}>
              <div className="text-[#00FF00] text-xs font-mono uppercase tracking-widest mb-1">Target Institutions</div>
              <div className="text-[#A3A3A3] text-xs" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Regional Banks &bull; Community Banks &bull; Credit Unions
                <br />$500M – $10B in Assets
              </div>
            </div>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="pt-8 border-t border-[#1A1A1A] flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-[#A3A3A3] text-xs font-mono"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}>
            &copy; 2025 NexGenPay &bull; Next Generation Payments &bull; Service Excellence
          </p>
          <div className="flex gap-6">
            <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer"
              className="text-[#A3A3A3] text-xs hover:text-[#00FF00] transition-colors font-mono"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Privacy Policy
            </a>
            <a href={TERMS_URL} target="_blank" rel="noopener noreferrer"
              className="text-[#A3A3A3] text-xs hover:text-[#00FF00] transition-colors font-mono"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Terms of Service
            </a>
            <a href="mailto:calvin@nexgenpay.co"
              className="text-[#A3A3A3] text-xs hover:text-[#00FF00] transition-colors font-mono"
              style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Contact
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
